import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { Button } from './Button';
import { SegmentedBar } from './ProgressBar';

export interface CoachmarkProps {
  open: boolean;
  /**
   * CSS selector for the element to spotlight. Several matches are allowed —
   * the nav rail and the mobile dock render the same `data-tour-nav` items
   * simultaneously, only one of which is actually visible — the first match
   * with a non-zero rect wins. Omit for a centred, non-pointing step (welcome,
   * finish).
   */
  targetSelector?: string;
  /**
   * CSS selector for a "reveal" toggle to click when `targetSelector` matches
   * nothing yet — e.g. the mobile dock's "Mehr" overflow button, which must
   * be expanded before a folded nav item exists in the DOM at all. No-op if
   * the target is already visible or the selector matches nothing.
   */
  revealSelector?: string;
  title: ReactNode;
  body: ReactNode;
  step: number;
  stepCount: number;
  onNext?: () => void;
  onPrev?: () => void;
  onSkip: () => void;
  nextLabel?: ReactNode;
  prevLabel?: ReactNode;
  finishLabel?: ReactNode;
  skipLabel?: ReactNode;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** First match whose rect actually has size — filters out the hidden twin (dock vs. rail). */
function findVisibleElement(selector: string): HTMLElement | null {
  const candidates = document.querySelectorAll<HTMLElement>(selector);
  for (const el of candidates) {
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return el;
  }
  return null;
}

function toRect(el: HTMLElement): Rect {
  const rect = el.getBoundingClientRect();
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

/**
 * A `position: sticky`/`fixed` target (the resource bar, the nav rail) is
 * already wherever it always is — calling `scrollIntoView` on it anyway
 * scrolls the page trying to center its *static* flow position while the
 * element itself stays visually pinned, leaving the measured rect briefly
 * out of sync with what's on screen. Only scroll for targets that actually
 * need it.
 */
function isFullyInViewport(rect: Rect): boolean {
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.top + rect.height <= window.innerHeight &&
    rect.left + rect.width <= window.innerWidth
  );
}

const PAD = 8;

/**
 * Spotlight overlay for the first-run guided tour.
 *
 * Dims the screen and cuts a highlighted hole around one real UI element,
 * with a callout describing what it does. Built from four dimming bars
 * around the target rect rather than an SVG mask — simpler, and avoids any
 * cross-browser mask-support questions for a one-off overlay.
 *
 * Rendered via a portal into `document.body` rather than inline where it's
 * called — `position: fixed; inset: 0` is only guaranteed to cover the true
 * viewport when NO ancestor establishes its own containing block, and that's
 * an easy invariant for a caller several levels up the tree to accidentally
 * break (a `transform`, `filter`, `contain`, or similar on any ancestor, now
 * or in a future change, silently shrinks/offsets the "fixed" box to that
 * ancestor's box instead). A portal makes the overlay immune to that by
 * construction — it always measures against the real viewport, matching
 * `getBoundingClientRect()` on the spotlighted target, which is also
 * viewport-relative.
 */
export function Coachmark({
  open,
  targetSelector,
  revealSelector,
  title,
  body,
  step,
  stepCount,
  onNext,
  onPrev,
  onSkip,
  nextLabel = 'Weiter',
  prevLabel = 'Zurück',
  finishLabel = 'Fertig',
  skipLabel = 'Tour überspringen',
}: CoachmarkProps) {
  const [rect, setRect] = useState<Rect | null>(null);
  const calloutRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    if (!targetSelector) {
      setRect(null);
      return;
    }
    let scrolledIntoView = false;
    const update = () => {
      const el = findVisibleElement(targetSelector);
      if (!el && revealSelector) {
        // Target doesn't exist yet — e.g. it's folded behind the mobile
        // dock's "Mehr" overflow toggle. Expand it and let the next
        // re-measure (the settle timeout, or the resulting layout's own
        // resize/scroll events) find the now-rendered target.
        const reveal = document.querySelector<HTMLElement>(revealSelector);
        if (reveal && reveal.getAttribute('aria-expanded') === 'false') reveal.click();
      }
      const elRect = el ? toRect(el) : null;
      if (elRect && !scrolledIntoView && !isFullyInViewport(elRect)) {
        scrolledIntoView = true;
        // jsdom (unit tests) doesn't implement scrollIntoView.
        el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }
      setRect(elRect);
    };
    update();
    // Layout can still be settling right after a view switch (fonts, images,
    // queue badges) — one delayed re-measure covers that without a full poll loop.
    const settle = window.setTimeout(update, 120);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.clearTimeout(settle);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, targetSelector, revealSelector, step]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onSkip();
      else if (event.key === 'ArrowRight' && onNext) onNext();
      else if (event.key === 'ArrowLeft' && onPrev) onPrev();
    };
    document.addEventListener('keydown', onKeyDown);
    calloutRef.current?.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onNext, onPrev, onSkip]);

  if (!open) return null;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;

  const hole = rect
    ? {
        top: Math.max(0, rect.top - PAD),
        left: Math.max(0, rect.left - PAD),
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }
    : null;

  // Callout placement: below the hole if there's room, otherwise above; for a
  // centred step (no target) it just sits in the middle of the screen.
  const calloutWidth = 352;
  let calloutStyle: CSSProperties;
  if (hole) {
    const spaceBelow = vh - (hole.top + hole.height);
    const placeBelow = spaceBelow > 180 || spaceBelow > hole.top;
    const rawLeft = hole.left + hole.width / 2 - calloutWidth / 2;
    const left = Math.min(Math.max(12, rawLeft), vw - calloutWidth - 12);
    calloutStyle = placeBelow
      ? { top: hole.top + hole.height + 14, left }
      : { top: Math.max(12, hole.top - 14), left, transform: 'translateY(-100%)' };
  } else {
    calloutStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  return createPortal(
    <div className="fixed inset-0 z-modal" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : 'Geführte Tour'}>
      {hole ? (
        <>
          <div className="absolute inset-x-0 top-0 bg-void/75" style={{ height: hole.top }} aria-hidden />
          <div
            className="absolute inset-x-0 bottom-0 bg-void/75"
            style={{ top: hole.top + hole.height }}
            aria-hidden
          />
          <div
            className="absolute bg-void/75"
            style={{ top: hole.top, left: 0, width: hole.left, height: hole.height }}
            aria-hidden
          />
          <div
            className="absolute bg-void/75"
            style={{ top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute rounded-control border-2 border-silicate shadow-glow"
            style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
            aria-hidden
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-void/75" onClick={onSkip} aria-hidden />
      )}

      <div
        ref={calloutRef}
        tabIndex={-1}
        className={cn(
          'absolute w-[352px] max-w-[calc(100vw-24px)] rounded-panel border border-edge-strong bg-plate-raised p-4',
          'shadow-panel-lg outline-none',
        )}
        style={calloutStyle}
      >
        <div className="mb-2 flex items-center justify-between gap-3">
          <SegmentedBar value={step} max={stepCount} accent="silicate" label={`${step} / ${stepCount}`} className="min-w-0 flex-1" />
          {/* Skip lives up here, separate from Prev/Next below, so the
              footer row never has to fit three buttons' worth of German
              labels ("Tour überspringen") in one line. */}
          <Button variant="ghost" size="sm" className="shrink-0" onClick={onSkip}>
            {skipLabel}
          </Button>
        </div>
        <h3 className="font-display text-sm font-semibold uppercase tracking-[0.1em] text-content">{title}</h3>
        <p className="mt-1.5 text-xs leading-relaxed text-content-secondary">{body}</p>
        <div className="mt-3.5 flex flex-wrap items-center justify-end gap-2">
          {onPrev ? (
            <Button variant="secondary" size="sm" onClick={onPrev}>
              {prevLabel}
            </Button>
          ) : null}
          <Button variant="primary" size="sm" onClick={onNext ?? onSkip}>
            {onNext ? nextLabel : finishLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
